import { countryNamePt, countryFlag } from "@/lib/country-names";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarDays, MapPin, Loader2 } from "lucide-react";
import { listPublishedOriginals, recordOriginalView, type OriginalRouteCard } from "@/lib/originals.functions";
import { recordOriginalViewOncePerSession } from "@/lib/originals-view-hook";
import { formatOriginalPrice } from "@/lib/originals";

export const Route = createFileRoute("/originals/")({
  component: OriginalsPage,
  head: () => ({
    meta: [
      { title: "Routify Originals — rotas prontas feitas pela equipa" },
      {
        name: "description",
        content:
          "Rotas de viagem completas, testadas e escritas pela equipa Routify. Compras uma vez e fica na tua conta para sempre.",
      },
      { property: "og:title", content: "Routify Originals — rotas prontas" },
      {
        property: "og:description",
        content: "Rotas completas feitas pela equipa Routify. Compra à unidade e usa quando quiseres.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

function OriginalsPage() {
  const listFn = useServerFn(listPublishedOriginals);
  const [routes, setRoutes] = useState<OriginalRouteCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    recordOriginalViewOncePerSession("store");
  }, []);

  useEffect(() => {
    let alive = true;
    listFn()
      .then((data) => alive && setRoutes(data))
      .catch(() => alive && setError("Não foi possível carregar as rotas. Tenta novamente."));
    return () => {
      alive = false;
    };
  }, [listFn]);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">Routify Originals</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Rotas prontas, feitas pela equipa</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Cada rota é escrita e testada por nós, com dias completos, dicas próprias e alternativas
          para quando algo corre mal. Compras uma vez e fica na tua conta.
        </p>
      </header>

      {error && <p className="rounded-lg border border-destructive/40 p-4 text-sm text-destructive">{error}</p>}

      {!routes && !error && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> A carregar rotas…
        </div>
      )}

      {routes && routes.length === 0 && (
        <p className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Ainda não há rotas à venda. Estamos a preparar as primeiras.
        </p>
      )}

      {routes && routes.length > 0 && (() => {
        const groups = groupByCountry(routes);
        return (
          <>
            {groups.length > 1 && (
              <nav className="mb-8 flex flex-wrap gap-2" aria-label="Países">
                {groups.map((g) => (
                  <a
                    key={g.code}
                    href={`#pais-${g.code}`}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-sm transition hover:border-brand/50"
                  >
                    {g.flag} {g.name} ({g.routes.length})
                  </a>
                ))}
              </nav>
            )}
            <div className="space-y-10">
              {groups.map((g) => (
                <section key={g.code} id={`pais-${g.code}`} className="scroll-mt-6">
                  <h2 className="mb-4 flex items-center gap-2 border-b border-border pb-2 text-xl font-bold">
                    <span aria-hidden>{g.flag}</span> {g.name}
                    <span className="text-sm font-normal text-muted-foreground">
                      · {g.routes.length} rota{g.routes.length === 1 ? "" : "s"}
                    </span>
                  </h2>
                  <CardGrid routes={g.routes} />
                </section>
              ))}
            </div>
          </>
        );
      })()}
    </main>
  );
}

const COUNTRY_NAMES = new Proxy({} as Record<string, string>, {
  get: (_t, code: string) => countryNamePt(code),
});

const flagOf = countryFlag;

function groupByCountry(routes: OriginalRouteCard[]) {
  const map = new Map<string, OriginalRouteCard[]>();
  for (const r of routes) {
    const code = (r.countryCode ?? "").trim().toUpperCase() || "XX";
    if (!map.has(code)) map.set(code, []);
    map.get(code)!.push(r);
  }
  return [...map.entries()]
    .map(([code, list]) => ({
      code,
      routes: list,
      flag: flagOf(code),
      name: code === "XX" ? "Outros destinos" : COUNTRY_NAMES[code] ?? code,
    }))
    .sort((a, b) =>
      a.code === "XX" ? 1 : b.code === "XX" ? -1 : b.routes.length - a.routes.length || a.name.localeCompare(b.name, "pt"),
    );
}

function CardGrid({ routes }: { routes: OriginalRouteCard[] }) {
  return (
      <div className="grid gap-4 sm:grid-cols-2">
        {routes.map((route) => (
          <Link
            key={route.id}
            to="/originals/$slug"
            params={{ slug: route.slug }}
            className="group overflow-hidden rounded-xl border border-border bg-card transition hover:border-brand/50"
          >
            {route.heroImageUrl && (
              <img
                src={route.heroImageUrl}
                alt={`Capa da rota ${route.title}`}
                loading="lazy"
                className="h-40 w-full object-cover"
              />
            )}
            <div className="space-y-2 p-4">
              <h2 className="text-lg font-semibold leading-tight">{route.title}</h2>
              {route.subtitle && <p className="text-sm text-muted-foreground">{route.subtitle}</p>}
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {route.destination}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" /> {route.days} dia{route.days === 1 ? "" : "s"}
                </span>
                <span>{route.stopsCount} paragens</span>
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="text-base font-semibold">
                  {formatOriginalPrice(route.priceCents, route.currency)}
                </span>
                <span className="inline-flex items-center gap-1 text-sm text-brand">
                  Ver rota <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
  );
}
